import { MapLineweightMode } from '@/graphql/graphql';

/**
 * CAD lineweights on the 2D map, following AutoCAD's model space.
 *
 * GDAL's DXF/DWG drivers write a lineweight into OGR_STYLE as `PEN(w:0.09g)`:
 * the number is the lineweight in **millimetres** even though the unit says
 * `g` (ground). We used to draw it as metres, so a 0.09 mm line was 9 cm wide
 * on the ground and fattened with every zoom step.
 *
 * The modes (`UserViewerSettings.mapLineweightMode`):
 * - SCREEN: a fixed on-screen width, the lineweight at 96 DPI, never under
 *   1 px. 0.25 mm and below draw as 1 px, as in AutoCAD.
 * - ZOOM: the lineweight as plotted at 1:1000 (1 mm of paper = 1 m of
 *   ground), so lines thicken as you zoom in. Never under 1 px.
 * - HAIRLINE: every data line 1 px.
 *
 * Lines the app styles itself (rainfall results, …) and layers with their own
 * 2D line width carry `_lineWidthPx` and keep it in every mode (still ≥ 1 px).
 */

const DPI = 96;
const MM_PER_INCH = 25.4;

/** AutoCAD's LWDEFAULT: a CAD line that sets no lineweight draws at this. */
export const CAD_DEFAULT_LINEWEIGHT_MM = 0.25;

/** A line with no CAD pen at all (shapefile, GeoJSON, …): 2 px in SCREEN. */
export const UNSTYLED_LINEWEIGHT_MM = (2 * MM_PER_INCH) / DPI;

/** Ground metres per lineweight millimetre in ZOOM mode (1:1000). */
const ZOOM_METRES_PER_MM = 1;

/**
 * Metres per pixel at zoom 0 on the equator. Mapbox GL draws 512 px tiles, so
 * this is half the 256 px-tile 156 543 m that `s0` / `s22` in core/style use.
 */
const GL_METRES_PER_PX_Z0 = 78271.51696402048;

export function pxPerMetre(lat: number, zoom: number): number {
  const φ = (Math.max(-85.05, Math.min(85.05, lat)) * Math.PI) / 180;
  return Math.pow(2, zoom) / (GL_METRES_PER_PX_Z0 * Math.cos(φ));
}

export function lineweightMmToPx(mm: number): number {
  return Math.max(1, (mm * DPI) / MM_PER_INCH);
}

/**
 * The `line-width` paint expression for data lines. Reads `_lineWidthPx` (a
 * fixed width, wins) and `_lineWeightMm` (falls back to `fallbackMm`).
 * `extraPx` widens every line, for the selection highlight drawn under it.
 * `lat` only matters for ZOOM, where ground metres become pixels.
 */
export function mapLineWidthExpression(
  mode: MapLineweightMode,
  lat: number,
  { extraPx = 0, fallbackMm = UNSTYLED_LINEWEIGHT_MM }: { extraPx?: number; fallbackMm?: number } = {},
): unknown {
  const mm = ['coalesce', ['get', '_lineWeightMm'], fallbackMm];
  // Every width, a fixed one too, is floored at 1 px: thinner reads as gone.
  const withFixed = (px: unknown) => ['+', ['max', 1, ['coalesce', ['get', '_lineWidthPx'], px]], extraPx];

  if (mode === MapLineweightMode.Hairline) return withFixed(1);
  if (mode === MapLineweightMode.Screen) return withFixed(['max', 1, ['*', mm, DPI / MM_PER_INCH]]);

  // ZOOM: one stop per integer zoom. Between stops the width doubles per zoom
  // level, which exponential base 2 reproduces exactly; the 1 px floor is
  // applied at each stop, so it holds to within a zoom level.
  const stops: unknown[] = [];
  for (let z = 0; z <= 22; z++) {
    stops.push(z, withFixed(['max', 1, ['*', mm, ZOOM_METRES_PER_MM * pxPerMetre(lat, z)]]));
  }
  return ['interpolate', ['exponential', 2], ['zoom'], ...stops];
}

const ORDER: MapLineweightMode[] = [MapLineweightMode.Screen, MapLineweightMode.Zoom, MapLineweightMode.Hairline];

export const MAP_LINEWEIGHT_MODES = ORDER;

/** `map.lineweight.<key>` / `<key>Description` labels (project namespace). */
export const MAP_LINEWEIGHT_MODE_KEY: Record<MapLineweightMode, 'screen' | 'zoom' | 'hairline'> = {
  [MapLineweightMode.Screen]: 'screen',
  [MapLineweightMode.Zoom]: 'zoom',
  [MapLineweightMode.Hairline]: 'hairline',
};

export function nextMapLineweightMode(mode: MapLineweightMode): MapLineweightMode {
  return ORDER[(ORDER.indexOf(mode) + 1) % ORDER.length];
}
